import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, getDocs, getDoc, orderBy, where, doc, updateDoc, serverTimestamp, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../hooks/useToast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReservationAttachmentFields from '../../../components/admin/facility-reservations/ReservationAttachmentFields';
import { emptyAttachmentChanges, saveReservationWithAttachments } from '../../../utils/reservationAttachments';
import { calculateFinalPriceWithVAT, findDiscountByMemberType } from '../../../utils/pricing';
import SmsService from '../../../services/SmsService';

// 타입 정의
interface FilterState {
  facilityId: string;
  userName: string;
  status: string;
  startDate: Date | null;
  endDate: Date | null;
}

interface ManualUserInfo {
  name: string;
  email: string;
  organization: string;
  phoneNumber: string;
}

interface ReservationFormState {
  userSelectionType: 'existing' | 'manual';
  selectedUserId: string;
  selectedUserName: string;
  manualUserInfo: ManualUserInfo;
  facilityId: string;
  reservationDate: string;
  startTime: number;
  endTime: number;
}

interface Reservation {
  id: string;
  facilityId: string;
  facilityName: string;
  userId: string;
  userName: string;
  organization: string;
  phoneNumber: string;
  reservationDate: Date;
  startTimeHour: number;
  endTimeHour: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: Date;
  updatedAt?: Date;
  hasDiscountRequest?: boolean;
  discountReasonCategory?: string;
  discountReasonDetail?: string;
}

interface User {
  uid: string;
  name: string;
  email: string;
  organization: string;
  phoneNumber?: string;
  userLevel: number;
}

interface Facility {
  id: string;
  name: string;
  reservationType: 'time-slot' | 'start-end';
  availableTimeSlots?: number[];
  operatingHours: { start: number; end: number };
  reservationTimeUnit: number;
  price: number;
  basePrice?: number;
  maxPrice?: number;
  discounts?: Array<{ memberType: string | number; discountRate: number }>;
}

// 상수 정의
const ITEMS_PER_PAGE = 30;
const MAX_VISIBLE_PAGES = 5;
const MAX_USER_SEARCH_RESULTS = 10;
const NEW_RESERVATION_ALERT_DURATION = 5000;
const SUCCESS_MESSAGE_DELAY = 1500;

// 수정된 인터페이스
interface Reservation {
  id: string;
  facilityId: string;
  facilityName: string;
  userId: string;
  userName: string;
  organization: string;
  phoneNumber: string;
  reservationDate: Date;
  startTimeHour: number;
  endTimeHour: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: Date;
  updatedAt?: Date;
  userLevel?: number;
  discountType?: string;
  applicationFormUrl?: string;
}

interface FilterState {
  facilityId: string;
  userName: string;
  status: string;
  startDate: Date | null;
  endDate: Date | null;
}

const FacilityReservationsPage: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [filteredReservations, setFilteredReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<FilterState>({
    facilityId: '',
    userName: '',
    status: '',
    startDate: null,
    endDate: null
  });
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState<string | null>(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // 예약 폼 상태
  const [reservationForm, setReservationForm] = useState<ReservationFormState>({
    userSelectionType: 'existing',
    selectedUserId: '',
    selectedUserName: '',
    manualUserInfo: {
      name: '',
      email: '',
      organization: '',
      phoneNumber: ''
    },
    facilityId: '',
    reservationDate: '',
    startTime: 9,
    endTime: 10
  });
  
  // 선택된 시설물 정보
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const reservationCreateRef = useRef<ReturnType<typeof doc> | null>(null);
  const [attachmentChanges, setAttachmentChanges] = useState(emptyAttachmentChanges);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  
  // 사용자 검색 상태
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // 새로운 예약 알림 상태
  const [newReservationAlert, setNewReservationAlert] = useState(false);
  const [previousReservationCount, setPreviousReservationCount] = useState(0);

  // 권한 체크 및 예약 데이터 로드
  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 시설물 예약 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.facilityReservations)) {
        navigate('/admin/dashboard');
        return;
      }

      const q = query(
        collection(db, 'reservations'),
        orderBy('createdAt', 'desc')
      );

      // 실시간 리스너 설정
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        try {
          const reservationsData = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              facilityId: data.facilityId || '',
              facilityName: data.facilityName || '',
              userId: data.uid || '',
              userName: data.name || '',
              organization: data.organization || '',
              phoneNumber: data.phoneNumber || '',
              reservationDate: data.reservationDate?.toDate ? data.reservationDate.toDate() : new Date(),
              startTimeHour: data.startTime || 0,
              endTimeHour: data.endTime || 0,
              status: data.status || 'pending',
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
              userLevel: data.userLevel || 9,
              discountType: data.discountType || null,
              applicationFormUrl: data.applicationFormUrl || ''
            };
          });

          // 새로운 예약 감지 로직
          if (previousReservationCount > 0 && reservationsData.length > previousReservationCount) {
            setNewReservationAlert(true);
            showToast('새로운 예약 신청이 접수되었습니다! 📋✨', { type: 'info' });
            setTimeout(() => setNewReservationAlert(false), NEW_RESERVATION_ALERT_DURATION);
          }
          setPreviousReservationCount(reservationsData.length);

          setReservations(reservationsData);
          setFilteredReservations(reservationsData);
          setError(null);
          setLoading(false);
        } catch (err: unknown) {
          console.error('예약 목록 조회 실패:', err);
          const errorMessage = err instanceof Error ? err.message : '예약 목록을 불러오는데 실패했습니다.';
          setError(errorMessage);
          showToast(errorMessage, { type: 'error' });
          setLoading(false);
        }
      }, (err) => {
        console.error('실시간 예약 목록 구독 실패:', err);
        const errorMessage = err instanceof Error ? err.message : '실시간 데이터 연결에 실패했습니다.';
        setError(errorMessage);
        showToast(errorMessage, { type: 'error' });
        setLoading(false);
      });

      // 컴포넌트 언마운트 시 리스너 정리
      return () => {
        unsubscribe();
      };
    }
  }, [user, userLevel, permissions, authLoading, navigate, previousReservationCount, showToast]);

  // 사용자와 시설물 데이터 로드
  const fetchUsersAndFacilities = useCallback(async () => {
    try {
      // 사용자 목록 가져오기
      const usersQuery = query(
        collection(db, 'user_profiles'),
        orderBy('name', 'asc')
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          name: data.name || '',
          email: data.email || '',
          organization: data.organization || '',
          phoneNumber: data.phoneNumber || '',
          userLevel: Number(data.userLevel) || 9
        };
      });
      setUsers(usersData);

      // 시설물 목록 가져오기
      const facilitiesQuery = query(
        collection(db, 'facilities'),
        where('available', '==', true),
        orderBy('name', 'asc')
      );
      const facilitiesSnapshot = await getDocs(facilitiesQuery);
      const facilitiesData = facilitiesSnapshot.docs.map(doc => {
        const data = doc.data();

        // availableTimeSlots가 없는 경우 기본값 생성
        let availableTimeSlots = data.availableTimeSlots || [];
        if (data.reservationType === 'time-slot' && availableTimeSlots.length === 0) {
          const start = data.operatingHours?.start || 9;
          const end = data.operatingHours?.end || 18;
          const timeUnit = data.reservationTimeUnit || 1;

          for (let hour = start; hour < end; hour += timeUnit) {
            availableTimeSlots.push(hour);
          }
        }

        return {
          id: doc.id,
          name: data.name || '',
          reservationType: data.reservationType || 'start-end',
          operatingHours: data.operatingHours || { start: 9, end: 18 },
          price: data.price ?? 0,
          basePrice: data.basePrice,
          maxPrice: data.maxPrice,
          discounts: data.discounts || [],
          reservationTimeUnit: data.reservationTimeUnit || 1,
          availableTimeSlots: availableTimeSlots.length > 0 ? availableTimeSlots : undefined
        };
      });
      setFacilities(facilitiesData);
    } catch (err: unknown) {
      console.error('사용자 및 시설물 목록 조회 실패:', err);
      const errorMessage = err instanceof Error ? err.message : '사용자 및 시설물 데이터 로드에 실패했습니다.';
      showToast(errorMessage, { type: 'error' });
    }
  }, [showToast]);

  // 사용자 목록과 시설물 목록 로드
  useEffect(() => {
    if (!authLoading && user) {
      fetchUsersAndFacilities();
    }
  }, [authLoading, user, fetchUsersAndFacilities]);

  // 사용자 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-search-container')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, [showUserDropdown]);

  // 사용자 검색 필터링 (메모이제이션)
  const filteredUsersList = useMemo(() => {
    if (userSearchQuery.trim() === '') {
      return [];
    }
    const query = userSearchQuery.toLowerCase();
    return users.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.organization.toLowerCase().includes(query)
    ).slice(0, MAX_USER_SEARCH_RESULTS);
  }, [userSearchQuery, users]);

  // 사용자 검색 결과 업데이트
  useEffect(() => {
    setFilteredUsers(filteredUsersList);
    setShowUserDropdown(filteredUsersList.length > 0 && userSearchQuery.trim() !== '');
  }, [filteredUsersList, userSearchQuery]);

  // 사용자 선택 핸들러
  const handleUserSelect = useCallback((user: User) => {
    setReservationForm(prev => ({
      ...prev,
      selectedUserId: user.uid,
      selectedUserName: user.name
    }));
    setUserSearchQuery(`${user.name} (${user.organization})`);
    setShowUserDropdown(false);
  }, []);

  // 모달 닫기 핸들러
  const handleModalClose = useCallback(() => {
    if (isCreatingReservation) return;
    reservationCreateRef.current = null;
    setAttachmentChanges(emptyAttachmentChanges());
    setShowReservationModal(false);
    setReservationForm({
      userSelectionType: 'existing',
      selectedUserId: '',
      selectedUserName: '',
      manualUserInfo: {
        name: '',
        email: '',
        organization: '',
        phoneNumber: ''
      },
      facilityId: '',
      reservationDate: '',
      startTime: 9,
      endTime: 10
    });
    setSelectedFacility(null);
    // 검색 상태 초기화
    setUserSearchQuery('');
    setFilteredUsers([]);
    setShowUserDropdown(false);
  }, [isCreatingReservation]);

  // 수기 입력 필드 변경 핸들러
  const handleManualInfoChange = useCallback((field: string, value: string) => {
    setReservationForm(prev => ({
      ...prev,
      manualUserInfo: { ...prev.manualUserInfo, [field]: value }
    }));
  }, []);

  // 예약 폼 필드 변경 핸들러
  const handleReservationFormChange = useCallback((field: string, value: string | number) => {
    setReservationForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // 시간대형 시간 선택 핸들러
  const handleTimeSlotChange = useCallback((time: number) => {
    const endTime = time + (selectedFacility?.reservationTimeUnit || 1);
    setReservationForm(prev => ({
      ...prev,
      startTime: time,
      endTime: endTime
    }));
  }, [selectedFacility?.reservationTimeUnit]);

  // 검색 입력 핸들러
  const handleUserSearchChange = useCallback((value: string) => {
    setUserSearchQuery(value);
    if (value !== reservationForm.selectedUserName) {
      setReservationForm(prev => ({
        ...prev,
        selectedUserId: '',
        selectedUserName: ''
      }));
    }
    setShowUserDropdown(value.trim() !== '');
  }, [reservationForm.selectedUserName]);

  // 시설물 선택 핸들러
  const handleFacilitySelect = useCallback((facilityId: string) => {
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacility(facility);
      setReservationForm(prev => ({
        ...prev,
        facilityId: facilityId,
        // 시설물 타입에 따라 시간 초기화
        startTime: facility.reservationType === 'time-slot' && facility.availableTimeSlots
          ? facility.availableTimeSlots[0]
          : facility.operatingHours.start,
        endTime: facility.reservationType === 'time-slot' && facility.availableTimeSlots
          ? facility.availableTimeSlots[0] + facility.reservationTimeUnit
          : facility.operatingHours.start + 1
      }));
    } else {
      setSelectedFacility(null);
      setReservationForm(prev => ({
        ...prev,
        facilityId: '',
        startTime: 9,
        endTime: 10
      }));
    }
  }, [facilities]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-search-container')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserDropdown]);

  // 필터링된 예약 목록 (메모이제이션)
  const filteredReservationsList = useMemo(() => {
    let filtered = [...reservations];

    if (filters.facilityId) {
      filtered = filtered.filter(res => res.facilityName === filters.facilityId);
    }

    if (filters.userName) {
      const searchTerm = filters.userName.toLowerCase();
      filtered = filtered.filter(res =>
        res.userName.toLowerCase().includes(searchTerm)
      );
    }

    // 기간 필터
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(res => {
        const resDate = new Date(res.reservationDate);
        resDate.setHours(0, 0, 0, 0);
        return resDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(res => {
        const resDate = new Date(res.reservationDate);
        return resDate <= endDate;
      });
    }

    if (filters.status) {
      filtered = filtered.filter(res => res.status === filters.status);
    } else {
      // status가 '전체'인 경우의 정렬 로직
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const pastReservations = filtered.filter(res => {
        const resDate = new Date(res.reservationDate);
        resDate.setHours(0, 0, 0, 0);
        return resDate < today;
      });

      const futureReservations = filtered.filter(res => {
        const resDate = new Date(res.reservationDate);
        resDate.setHours(0, 0, 0, 0);
        return resDate >= today;
      });

      futureReservations.sort((a, b) => a.reservationDate.getTime() - b.reservationDate.getTime());
      pastReservations.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      filtered = [...futureReservations, ...pastReservations];
    }

    return filtered;
  }, [filters, reservations]);

  // 필터링 결과 및 페이지네이션 업데이트
  useEffect(() => {
    setTotalPages(Math.ceil(filteredReservationsList.length / ITEMS_PER_PAGE));
    setCurrentPage(1);
    setFilteredReservations(filteredReservationsList);
  }, [filteredReservationsList]);

  // 현재 페이지에 표시할 예약 목록 계산 (메모이제이션)
  const getCurrentPageReservations = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredReservations.slice(startIndex, endIndex);
  }, [currentPage, filteredReservations]);

  // 페이지 변경 핸들러
  const handlePageChange = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 페이지네이션 컴포넌트
  const Pagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center items-center space-x-2 mt-4 mb-6">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
        >
          처음
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
        >
          이전
        </button>
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => handlePageChange(number)}
            className={`px-3 py-1 rounded border ${
              currentPage === number
                ? 'bg-blue-600 text-white'
                : 'border-gray-300 hover:bg-gray-100'
            }`}
          >
            {number}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
        >
          다음
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
        >
          마지막
        </button>
      </div>
    );
  };

  const handleFilterChange = useCallback((field: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // 날짜 선택 핸들러
  const handleDateChange = useCallback((dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setFilters(prev => ({
      ...prev,
      startDate: start,
      endDate: end
    }));
  }, []);

  // 감면 사유 신청이 있는지 확인하는 함수
  const hasDiscountRequest = useCallback((reservation: Reservation): boolean => {
    // discountType이 있고, userLevel이 9(일반사용자)이면서 discountType이 설정되어 있는 경우
    return !!(reservation.discountType && reservation.userLevel === 9);
  }, []);

  const handleStatusUpdate = useCallback(async (reservationId: string, newStatus: 'approved' | 'rejected') => {
    try {
      setUpdating(reservationId);

      // 현재 예약 정보 찾기
      const currentReservation = reservations.find(res => res.id === reservationId);
      if (!currentReservation) {
        throw new Error('예약 정보를 찾을 수 없습니다.');
      }

      const reservationRef = doc(db, 'reservations', reservationId);
      const updatedAt = new Date();

      await updateDoc(reservationRef, {
        status: newStatus,
        updatedAt
      });

      const statusText = newStatus === 'approved' ? '승인' : '거절';
      showToast(`예약이 ${statusText}되었습니다.`, { type: 'success' });

      // 승인/거절 시 SMS 발송
      if ((newStatus === 'approved' || newStatus === 'rejected') && currentReservation.phoneNumber) {
        try {
          const reservationDate = currentReservation.reservationDate.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\. /g, '.').replace(/\.$/, '');

          const smsMessage = newStatus === 'approved'
            ? `[신항시설관리센터] ${currentReservation.userName} 님 ${reservationDate} ${currentReservation.facilityName} 예약이 승인되었습니다.`
            : `[신항시설관리센터] ${currentReservation.userName} 님 ${reservationDate} ${currentReservation.facilityName} 예약이 거절되었습니다.`;

          await SmsService.sendSMS(currentReservation.phoneNumber, smsMessage);
          console.log(`예약 ${statusText} SMS 발송 완료:`, currentReservation.phoneNumber);
        } catch (smsError) {
          console.error('SMS 발송 실패:', smsError);
          showToast(`예약은 ${statusText}되었으나 SMS 발송에 실패했습니다.`, { type: 'warning' });
        }
      }
    } catch (err: unknown) {
      console.error('상태 업데이트 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '상태 업데이트 중 오류가 발생했습니다.';
      showToast(errorMessage, { type: 'error' });
    } finally {
      setUpdating(null);
    }
  }, [reservations, showToast]);

  // 예약 중복 체크 함수
  const checkReservationConflict = useCallback(async (facilityId: string, reservationDate: string, startTime: number, endTime: number): Promise<boolean> => {
    try {
      const reservationDateObj = new Date(reservationDate);
      const startOfDay = new Date(reservationDateObj);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(reservationDateObj);
      endOfDay.setHours(23, 59, 59, 999);

      const conflictQuery = query(
        collection(db, 'reservations'),
        where('facilityId', '==', facilityId),
        where('reservationDate', '>=', Timestamp.fromDate(startOfDay)),
        where('reservationDate', '<=', Timestamp.fromDate(endOfDay)),
        where('status', '!=', 'cancelled')
      );

      const querySnapshot = await getDocs(conflictQuery);

      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        const existingStart = data.startTime || 0;
        const existingEnd = data.endTime || 0;

        // 시간 겹침 체크
        if ((startTime < existingEnd && endTime > existingStart)) {
          return true; // 충돌 발생
        }
      }

      return false; // 충돌 없음
    } catch (error: unknown) {
      console.error('예약 중복 체크 실패:', error);
      throw error;
    }
  }, []);

  // 예약 생성 하들러
  const handleCreateReservation = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCreatingReservation) return;
    setIsCreatingReservation(true);

    try {
      // 폼 유효성 검사
      if (reservationForm.userSelectionType === 'existing' && !reservationForm.selectedUserId) {
        showToast('사용자를 검색하여 선택해주세요.', { type: 'warning' });
        return;
      }

      if (!reservationForm.facilityId || !reservationForm.reservationDate) {
        showToast('시설물과 예약 날짜를 모두 선택해주세요.', { type: 'warning' });
        return;
      }

      // 시설물 정보 가져오기
      const listedFacility = facilities.find(f => f.id === reservationForm.facilityId);
      const facilitySnapshot = await getDoc(doc(db, 'facilities', reservationForm.facilityId));
      if (!listedFacility || !facilitySnapshot.exists() || facilitySnapshot.data().available === false) {
        showToast('선택한 시설물 정보를 찾을 수 없습니다.', { type: 'error' });
        return;
      }

      // 저장 시점의 시설 요금과 할인 설정을 사용한다.
      const facilityData = facilitySnapshot.data();
      const selectedFacilityInfo: Facility = {
        ...listedFacility,
        name: facilityData.name || listedFacility.name,
        reservationType: facilityData.reservationType || listedFacility.reservationType,
        reservationTimeUnit: facilityData.reservationTimeUnit || listedFacility.reservationTimeUnit,
        availableTimeSlots: facilityData.availableTimeSlots?.length ? facilityData.availableTimeSlots : listedFacility.availableTimeSlots,
        price: facilityData.price ?? 0,
        basePrice: facilityData.basePrice,
        maxPrice: facilityData.maxPrice,
        discounts: facilityData.discounts || []
      };
      const endTime = selectedFacilityInfo.reservationType === 'time-slot'
        ? reservationForm.startTime + selectedFacilityInfo.reservationTimeUnit
        : reservationForm.endTime;

      // 시설물 타입에 따른 유효성 검사
      if (selectedFacilityInfo.reservationType === 'start-end') {
        if (reservationForm.startTime >= reservationForm.endTime) {
          showToast('종료 시간이 시작 시간보다 늦어야 합니다.', { type: 'warning' });
          return;
        }
      } else if (selectedFacilityInfo.reservationType === 'time-slot') {
        if (!selectedFacilityInfo.availableTimeSlots?.includes(reservationForm.startTime)) {
          showToast('유효하지 않은 시간대입니다.', { type: 'warning' });
          return;
        }
      }

      // 예약 중복 체크
      const hasConflict = await checkReservationConflict(
        reservationForm.facilityId,
        reservationForm.reservationDate,
        reservationForm.startTime,
        endTime
      );

      if (hasConflict) {
        showToast('선택한 시간대에 이미 예약이 있습니다. 다른 시간을 선택해주세요.', { type: 'warning' });
        return;
      }

      // 사용자 정보 가져오기
      let userInfo;
      if (reservationForm.userSelectionType === 'existing') {
        const selectedUser = users.find(u => u.uid === reservationForm.selectedUserId);
        if (!selectedUser) {
          showToast('선택한 사용자 정보를 찾을 수 없습니다.', { type: 'error' });
          return;
        }
        const profileSnapshot = await getDoc(doc(db, 'user_profiles', selectedUser.uid));
        if (!profileSnapshot.exists()) {
          showToast('선택한 사용자가 더 이상 존재하지 않습니다. 다시 선택해주세요.', { type: 'error' });
          return;
        }
        const selectedUserLevel = Number(profileSnapshot.data().userLevel) || 9;
        userInfo = {
          userLevel: selectedUserLevel,
          uid: selectedUser.uid,
          name: selectedUser.name,
          email: selectedUser.email,
          organization: selectedUser.organization,
          phoneNumber: selectedUser.phoneNumber
        };
      } else {
        // 수기 입력 유효성 검사
        const { name, email, organization, phoneNumber } = reservationForm.manualUserInfo;
        if (!name.trim() || !email.trim() || !organization.trim() || !phoneNumber.trim()) {
          showToast('수기 입력 사용자 정보를 모두 입력해주세요.', { type: 'warning' });
          return;
        }
        userInfo = {
          uid: 'manual_' + Date.now(),
          userLevel: 9,
          name: name.trim(),
          email: email.trim(),
          organization: organization.trim(),
          phoneNumber: phoneNumber.trim()
        };
      }

      const priceBreakdown = calculateFinalPriceWithVAT(selectedFacilityInfo, {
        reservationType: selectedFacilityInfo.reservationType,
        startTime: reservationForm.startTime,
        endTime,
        reservationTimeUnit: selectedFacilityInfo.reservationTimeUnit
      }, userInfo.userLevel);

      // 예약 데이터 생성
      const reservationData = {
        uid: userInfo.uid,
        name: userInfo.name,
        email: userInfo.email,
        phoneNumber: userInfo.phoneNumber || '',
        organization: userInfo.organization,
        facilityId: reservationForm.facilityId,
        facilityName: selectedFacilityInfo.name,
        reservationDate: Timestamp.fromDate(new Date(reservationForm.reservationDate)),
        startTime: reservationForm.startTime,
        endTime,
        reservationType: selectedFacilityInfo.reservationType,
        price: priceBreakdown.total,
        status: 'approved',
        userLevel: reservationForm.userSelectionType === 'existing' ? userInfo.userLevel : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Firestore에 예약 추가
      // Reuse the document ID after uncertain network failures to avoid duplicate records.
      const reservationRef = reservationCreateRef.current || doc(collection(db, 'reservations'));
      reservationCreateRef.current = reservationRef;
      const result = await saveReservationWithAttachments(reservationRef, userInfo.uid, null, attachmentChanges, reservationData, setSubmitStatus);
      if (result.cleanupFailed) showToast('예약은 저장되었으나 일부 파일 정리에 실패했습니다.', { type: 'warning' });
      reservationCreateRef.current = null;
      setAttachmentChanges(emptyAttachmentChanges());

      showToast(`${userInfo.name}님의 예약이 성공적으로 생성되었습니다.`, { type: 'success' });
      setShowReservationModal(false);

      // 폼 초기화
      setReservationForm({
        userSelectionType: 'existing',
        selectedUserId: '',
        selectedUserName: '',
        manualUserInfo: {
          name: '',
          email: '',
          organization: '',
          phoneNumber: ''
        },
        facilityId: '',
        reservationDate: '',
        startTime: 9,
        endTime: 10
      });

      // 검색 상태 초기화
      setUserSearchQuery('');
      setFilteredUsers([]);
      setShowUserDropdown(false);
      setSelectedFacility(null);
    } catch (err: unknown) {
      console.error('예약 생성 중 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '예약 생성 중 오류가 발생했습니다.';
      showToast(errorMessage, { type: 'error' });
    } finally {
      setIsCreatingReservation(false);
      setSubmitStatus('');
    }
  }, [reservationForm, facilities, users, checkReservationConflict, showToast, attachmentChanges, isCreatingReservation]);

  const selectedReservationUser = users.find(candidate => candidate.uid === reservationForm.selectedUserId);
  const previewUserLevel = reservationForm.userSelectionType === 'existing' ? selectedReservationUser?.userLevel : 9;
  const previewPrice = selectedFacility && previewUserLevel !== undefined && reservationForm.endTime > reservationForm.startTime
    ? calculateFinalPriceWithVAT(selectedFacility, {
        reservationType: selectedFacility.reservationType,
        startTime: reservationForm.startTime,
        endTime: selectedFacility.reservationType === 'time-slot'
          ? reservationForm.startTime + selectedFacility.reservationTimeUnit
          : reservationForm.endTime,
        reservationTimeUnit: selectedFacility.reservationTimeUnit
      }, previewUserLevel)
    : null;
  const previewDiscount = findDiscountByMemberType(selectedFacility?.discounts, previewUserLevel);

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  // 고유한 시설물 목록 추출
  const uniqueFacilities = Array.from(new Set(reservations.map(res => res.facilityName)));

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              시설물 예약 관리
            </div>
            <button
              onClick={() => navigate('/admin/facility-reservations/calendar')}
              className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
            >
              캘린더 보기
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 새로운 예약 알림 */}
        {newReservationAlert && (
          <div className="mt-4 bg-blue-100 border-l-4 border-blue-500 p-4 rounded-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-blue-700 text-sm font-medium font-['Pretendard']">
                  새로운 예약 신청이 접수되었습니다! 📋✨
                </p>
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => setNewReservationAlert(false)}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              시설물 예약을 관리하고 승인/거절할 수 있습니다. 필터를 사용하여 원하는 예약을 검색할 수 있습니다.
            </p>
          </div>
        </div>
        
        {/* 필터 섹션 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              예약 검색 및 필터
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                  시설물
                </label>
                <select
                  className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                  value={filters.facilityId}
                  onChange={(e) => handleFilterChange('facilityId', e.target.value)}
                >
                  <option value="">전체</option>
                  {uniqueFacilities.map((facility) => (
                    <option key={facility} value={facility}>
                      {facility}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                  예약자
                </label>
                <input
                  type="text"
                  className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                  placeholder="예약자 이름 검색"
                  value={filters.userName}
                  onChange={(e) => handleFilterChange('userName', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                  상태
                </label>
                <select
                  className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">전체</option>
                  <option value="pending">대기중</option>
                  <option value="approved">승인</option>
                  <option value="rejected">거절</option>
                  <option value="cancelled">취소됨</option>
                </select>
              </div>

              <div>
                <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                  예약 기간
                </label>
                <DatePicker
                  selectsRange={true}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onChange={handleDateChange}
                  dateFormat="yyyy.MM.dd"
                  placeholderText="기간 선택"
                  className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                  isClearable={true}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>
            </div>

            {/* 필터 초기화 버튼 */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setFilters({
                  facilityId: '',
                  userName: '',
                  status: '',
                  startDate: null,
                  endDate: null
                })}
                className="px-4 py-2 text-sm text-[#6b7684] hover:text-[#1e2c49] hover:bg-[#f8fbff] rounded-sm font-['Pretendard'] transition-colors"
              >
                필터 초기화
              </button>
            </div>
          </div>
        </div>

        {/* 예약 목록 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                예약 목록
              </h2>
              <button
                onClick={() => setShowReservationModal(true)}
                className="px-4 py-2 bg-[#155acb] text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
              >
                예약 추가하기
              </button>
            </div>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <div className="bg-white rounded-sm outline outline-1 outline-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#e5e8eb]">
                  <thead className="bg-[#f8fafc]">
                    <tr>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        시설물
                      </th>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        예약자
                      </th>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        소속
                      </th>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        예약 일시
                      </th>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        상태
                      </th>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        신청 일시
                      </th>
                      <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                        관리
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#e5e8eb]">
                    {getCurrentPageReservations.map((reservation) => (
                      <tr 
                        key={reservation.id} 
                        className="cursor-pointer hover:bg-[#f8fafc] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}>
                          <div className="text-base font-semibold font-['Pretendard'] text-[#1e2c49]">
                            {reservation.facilityName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}>
                          <div className="text-base font-['Pretendard'] text-[#6b7684]">{reservation.userName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}>
                          <div className="flex items-center gap-2 text-base font-['Pretendard'] text-[#6b7684]">
                            <span>{reservation.organization}</span>
                            {reservation.applicationFormUrl && (
                              <span
                                className="inline-flex"
                                title="대관신청서 첨부됨"
                                aria-label="대관신청서 첨부됨"
                              >
                                <svg
                                  className="h-4 w-4 text-[#6b7684]"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path d="M15.5 6.5v6.75a5.25 5.25 0 11-10.5 0V5.75a3.75 3.75 0 117.5 0v7a2.25 2.25 0 11-4.5 0V7a.75.75 0 011.5 0v5.75a.75.75 0 001.5 0v-7a2.25 2.25 0 10-4.5 0v7.5a3.75 3.75 0 007.5 0V6.5a.75.75 0 011.5 0z" />
                                </svg>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}>
                          <div className="text-base font-['Pretendard'] text-[#6b7684]">
                            {reservation.reservationDate.toLocaleDateString()} {reservation.startTimeHour}:00 ~ {reservation.endTimeHour}:00
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium font-['Pretendard']
                            ${reservation.status === 'approved' ? 'bg-green-100 text-green-800' :
                              reservation.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              reservation.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'}`}>
                            {reservation.status === 'approved' ? '승인' :
                             reservation.status === 'rejected' ? '거절' :
                             reservation.status === 'cancelled' ? '취소됨' : '대기중'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}>
                          <div className="text-base font-['Pretendard'] text-[#6b7684]">
                            {reservation.createdAt.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {reservation.status === 'pending' ? (
                            hasDiscountRequest(reservation) ? (
                              // 감면 사유 신청이 있는 경우 파란색 내역확인 버튼과 거절 버튼
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => navigate(`/admin/facility-reservations/${reservation.id}`)}
                                  className="w-[70px] h-8 bg-[#155acb] text-white rounded-sm hover:bg-[#155acb]/90 text-sm font-medium font-['Pretendard'] transition-colors flex items-center justify-center"
                                >
                                  내역확인
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(reservation.id, 'rejected')}
                                  disabled={updating === reservation.id}
                                  className="w-[50px] h-8 bg-red-500 text-white rounded-sm hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium font-['Pretendard'] transition-colors flex items-center justify-center"
                                >
                                  {updating === reservation.id ? '...' : '거절'}
                                </button>
                              </div>
                            ) : (
                              // 일반 예약인 경우 기존 승인/거절 버튼
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleStatusUpdate(reservation.id, 'approved')}
                                  disabled={updating === reservation.id}
                                  className="w-[50px] h-8 bg-green-500 text-white rounded-sm hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium font-['Pretendard'] transition-colors flex items-center justify-center"
                                >
                                  {updating === reservation.id ? '...' : '승인'}
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(reservation.id, 'rejected')}
                                  disabled={updating === reservation.id}
                                  className="w-[50px] h-8 bg-red-500 text-white rounded-sm hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium font-['Pretendard'] transition-colors flex items-center justify-center"
                                >
                                  {updating === reservation.id ? '...' : '거절'}
                                </button>
                              </div>
                            )
                          ) : (
                            <div className="text-base font-['Pretendard'] text-[#6b7684]">
                              {reservation.updatedAt?.toLocaleString() || '-'}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {getCurrentPageReservations.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-[#6b7684] text-lg font-medium font-['Pretendard']">
                    조건에 맞는 예약이 없습니다.
                  </div>
                  <div className="text-[#6b7684] text-sm font-['Pretendard'] mt-2">
                    필터 조건을 변경하거나 '필터 초기화'를 시도해 보세요.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-6">
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded-sm border border-[#e5e8eb] disabled:opacity-50 disabled:cursor-not-allowed text-[#1e2c49] font-['Pretendard'] hover:bg-[#f8fbff] transition-colors"
                >
                  처음
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded-sm border border-[#e5e8eb] disabled:opacity-50 disabled:cursor-not-allowed text-[#1e2c49] font-['Pretendard'] hover:bg-[#f8fbff] transition-colors"
                >
                  이전
                </button>
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(i);
                  }

                  return pageNumbers.map(number => (
                    <button
                      key={number}
                      onClick={() => handlePageChange(number)}
                      className={`px-3 py-2 rounded-sm border font-['Pretendard'] transition-colors ${
                        currentPage === number
                          ? 'bg-[#155acb] text-white border-[#155acb]'
                          : 'border-[#e5e8eb] text-[#1e2c49] hover:bg-[#f8fbff]'
                      }`}
                    >
                      {number}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded-sm border border-[#e5e8eb] disabled:opacity-50 disabled:cursor-not-allowed text-[#1e2c49] font-['Pretendard'] hover:bg-[#f8fbff] transition-colors"
                >
                  다음
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded-sm border border-[#e5e8eb] disabled:opacity-50 disabled:cursor-not-allowed text-[#1e2c49] font-['Pretendard'] hover:bg-[#f8fbff] transition-colors"
                >
                  마지막
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>

      {/* 예약 추가 모달 */}
      {showReservationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-[#1e2c49] mb-4">
              새 예약 추가
            </h3>
            
            <form onSubmit={handleCreateReservation} className="space-y-4">
              <fieldset disabled={isCreatingReservation} className="space-y-4">
              {/* 예약자 선택 방식 */}
              <div>
                <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                  예약자 선택 방식
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="existing"
                      checked={reservationForm.userSelectionType === 'existing'}
                      onChange={(e) => {
                        setReservationForm(prev => ({
                          ...prev,
                          userSelectionType: e.target.value as 'existing' | 'manual',
                          selectedUserId: '',
                          selectedUserName: ''
                        }));
                        // 검색 상태 초기화
                        setUserSearchQuery('');
                        setFilteredUsers([]);
                        setShowUserDropdown(false);
                      }}
                      className="mr-2"
                    />
                    기존 사용자 선택
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="manual"
                      checked={reservationForm.userSelectionType === 'manual'}
                      onChange={(e) => {
                        setReservationForm(prev => ({
                          ...prev,
                          userSelectionType: e.target.value as 'existing' | 'manual',
                          selectedUserId: '',
                          selectedUserName: ''
                        }));
                        // 검색 상태 초기화
                        setUserSearchQuery('');
                        setFilteredUsers([]);
                        setShowUserDropdown(false);
                      }}
                      className="mr-2"
                    />
                    수기 입력
                  </label>
                </div>
              </div>

              {/* 기존 사용자 선택 */}
              {reservationForm.userSelectionType === 'existing' && (
                <div className="relative user-search-container">
                  <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                    사용자 검색
                  </label>
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={(e) => handleUserSearchChange(e.target.value)}
                    onFocus={() => {
                      if (filteredUsers.length > 0) {
                        setShowUserDropdown(true);
                      }
                    }}
                    className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                    placeholder="사용자 이름, 이메일 또는 소속으로 검색..."
                    required
                  />
                  {showUserDropdown && filteredUsers.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border border-[#e5e8eb] rounded-sm shadow-lg mt-1 max-h-60 overflow-y-auto">
                      {filteredUsers.map(user => (
                        <div
                          key={user.uid}
                          onClick={() => handleUserSelect(user)}
                          className="px-3 py-2 cursor-pointer hover:bg-[#f8fafc] border-b border-[#f1f1f1] last:border-b-0"
                        >
                          <div className="font-medium text-[#1e2c49]">
                            {user.name}
                          </div>
                          <div className="text-sm text-[#6b7684]">
                            {user.organization} - {user.email}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {userSearchQuery && filteredUsers.length === 0 && showUserDropdown && (
                    <div className="absolute z-50 w-full bg-white border border-[#e5e8eb] rounded-sm shadow-lg mt-1 px-3 py-2 text-[#6b7684] text-sm">
                      검색 결과가 없습니다.
                    </div>
                  )}
                  <input
                    type="hidden"
                    value={reservationForm.selectedUserId}
                    required
                  />
                </div>
              )}

              {/* 수기 입력 */}
              {reservationForm.userSelectionType === 'manual' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      성명
                    </label>
                    <input
                      type="text"
                      value={reservationForm.manualUserInfo.name}
                      onChange={(e) => handleManualInfoChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      이메일
                    </label>
                    <input
                      type="email"
                      value={reservationForm.manualUserInfo.email}
                      onChange={(e) => handleManualInfoChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      소속
                    </label>
                    <input
                      type="text"
                      value={reservationForm.manualUserInfo.organization}
                      onChange={(e) => handleManualInfoChange('organization', e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      연락처
                    </label>
                    <input
                      type="tel"
                      value={reservationForm.manualUserInfo.phoneNumber}
                      onChange={(e) => handleManualInfoChange('phoneNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                </div>
              )}

              {/* 시설물 선택 */}
              <div>
                <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                  시설물
                </label>
                <select
                  value={reservationForm.facilityId}
                  onChange={(e) => handleFacilitySelect(e.target.value)}
                  className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                  required
                >
                  <option value="">시설물을 선택하세요</option>
                  {facilities.map(facility => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name} ({facility.reservationType === 'time-slot' ? '시간대형' : '시작종료형'})
                    </option>
                  ))}
                </select>
              </div>

              {/* 예약 일시 */}
              {selectedFacility?.reservationType === 'time-slot' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      예약 날짜
                    </label>
                    <input
                      type="date"
                      value={reservationForm.reservationDate}
                      onChange={(e) => handleReservationFormChange('reservationDate', e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      이용 시간대
                    </label>
                    <select
                      value={reservationForm.startTime}
                      onChange={(e) => handleTimeSlotChange(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    >
                      {selectedFacility.availableTimeSlots?.map(time => (
                        <option key={time} value={time}>
                          {String(time).padStart(2, '0')}:00 ~ {String(time + selectedFacility.reservationTimeUnit).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      예약 날짜
                    </label>
                    <input
                      type="date"
                      value={reservationForm.reservationDate}
                      onChange={(e) => handleReservationFormChange('reservationDate', e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      시작 시간
                    </label>
                    <select
                      value={reservationForm.startTime}
                      onChange={(e) => handleReservationFormChange('startTime', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {String(i).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e2c49] mb-2">
                      종료 시간
                    </label>
                    <select
                      value={reservationForm.endTime}
                      onChange={(e) => handleReservationFormChange('endTime', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-[#e5e8eb] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {String(i + 1).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {previewPrice && (
                <div className="rounded border border-blue-100 bg-blue-50 p-4 text-sm space-y-1" aria-label="예약 금액 안내">
                  <p className="font-bold">예상 예약 금액 (부가세 포함): {previewPrice.total.toLocaleString()}원</p>
                  <p>{reservationForm.userSelectionType === 'existing' ? `예약자 ${previewUserLevel}등급` : '수기 예약 · 일반 사용자 기준'} · 할인율 {previewDiscount?.discountRate ?? 0}%</p>
                  <p>공급가액 {previewPrice.net.toLocaleString()}원 + 부가세 {previewPrice.vat.toLocaleString()}원</p>
                  <p className="text-gray-600">최종 금액은 저장 시점의 사용자 등급과 시설 요금·할인 설정으로 계산됩니다.</p>
                </div>
              )}
              <ReservationAttachmentFields changes={attachmentChanges} onChange={setAttachmentChanges} disabled={isCreatingReservation} facilityName={selectedFacility?.name} />
              </fieldset>
              {submitStatus && <p role="status" className="text-sm text-blue-700">{submitStatus}</p>}
              {/* 버튼 */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleModalClose}
                  disabled={isCreatingReservation}
                  className="px-4 py-2 text-[#6b7684] border border-[#e5e8eb] rounded-sm hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isCreatingReservation}
                  className="px-4 py-2 bg-[#155acb] text-white rounded-sm hover:bg-[#155acb]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreatingReservation ? '생성 중...' : '예약 생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityReservationsPage; 
